<template>
  <div class="histogram-chart-wrapper">
      <div :id="chartId" class="histogram-chart" v-observe-visibility="visibilityChange" style="height: 200px; -webkit-backface-visibility: hidden;"></div>
      <div :id="`hover-info-${chartId}`" class="hover-info">
      <div class="info-wrapper" v-if="info.seats">
        <div class="info-label">Seats</div>
        <div class="info-value">
          <span style="color: #FA3138;">{{ info.seats }}R </span> /
          <span style="color: #66ABCF" v-if="chamber === 'senate'">{{ (majority * 2) - info.seats }}D </span>
          <span style="color: #66ABCF" v-else>{{ ((majority * 2) - 1) - info.seats }}D </span>
        </div>
      </div>
      <div class="info-wrapper" v-if="info.simulations">
        <div class="info-label">Simulations</div>
        <div class="info-value">{{ info.simulations }}</div>
        <div class="info-value">({{ info.percentage }}%)</div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import * as d3 from 'd3'

const props = {
  simulations: {
    type: Array,
    required: true
  },
  chamber: {
    type: String,
    required: true
  },
  majority: {
    type: Number,
    required: true
  },
  gopProb: {
    type: String,
    required: false
  },
  movement: {
    type: Object,
    required: false
  }
}

const computed = {
  ...mapGetters({
    screenWidth: 'ux/screenWidth',
    currentDate: 'aggregateModels/currentDate'
  }),
  chartId () {
    return `${this.chamber}-histogram-chart`
  }
}

const methods = {
  drawChart () {
    this.height = 200

    const chart = d3.select(`#${this.chartId}`)
    this.width = chart._groups[0][0].clientWidth

    chart.selectAll('*').remove()

    this.svg = chart.append('svg')
      .attr('width', this.width)
      .attr('height', this.height)
      .on('mouseleave', () => {
        d3.select(`#hover-info-${self.chartId}`).style('opacity', 0)
      })

    this.sims = this.simulations.filter(item => item.value > 100)

    const x = d3.scaleBand()
      .domain(this.sims.map(d => d.seats))
      .range([this.margin.left, this.width - this.margin.right]).padding(0.1)
    const y = d3.scaleLinear()
      .domain([0, d3.max(this.sims, d => d.value)])
      .range([this.height - this.margin.bottom, this.margin.top])

    let tickValues = null

    if (this.chamber === 'house') {
      tickValues = this.sims.map(item => item.seats)
        .filter(item => item % 10 === 0)
    } else {
      tickValues = this.sims.map(item => item.seats)
        .filter(item => item % 2 === 0)
    }

    const xAxis = g => g
      .attr('class', 'x-axis')
      .attr('transform', `translate(0, ${this.height - this.margin.bottom})`)
      .call(d3.axisBottom(x)
        .tickSizeOuter(0)
        .tickValues(tickValues)
      )

    this.svg.append('g').call(xAxis)

    const rects = this.svg.append('g')
      .selectAll('rect')
      .data(this.sims)
      .enter().append('rect')
      .attr('fill', (d) => d.seats >= this.majority ? '#FA3138' : '#66ABCF')
      .attr('x', d => x(d.seats))
      .attr('width', d => x.bandwidth() - 1)
      .attr('transform', `translate(0, -${this.margin.top})`)

    const overlayRects = this.svg.append('g')
      .selectAll('rect')
      .data(this.sims)
      .enter().append('rect')
      .attr('fill', 'rgb(189, 189, 189)')
      .style('opacity', 0)
      .attr('x', d => x(d.seats))
      .attr('width', d => x.bandwidth() - 1)
      .attr('transform', `translate(0, -${this.margin.top})`)

    const self = this

    overlayRects
      .on('mousemove', function (d) {
        d3.select(`#hover-info-${self.chartId}`).style('opacity', 1)
        d3.select(this).style('opacity', 0.2)
        self.info.seats = d.seats
        self.info.simulations = d.value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        self.info.percentage = ((d.value / 14000000) * 100).toFixed(2)
      })
      .on('mouseout', function (d) {
        d3.select(this).style('opacity', 0)
      })
      .attr('y', this.height - this.margin.bottom)
      .attr('height', 0)
      .transition()
      .delay((d, i) => this.chamber === 'senate' ? i * 100 : i * 20)
      .attr('y', 0)
      .attr('height', '100%')

    rects
      .attr('y', 0)
      .attr('height', 0)
      .transition()
      .delay((d, i) => this.chamber === 'senate' ? i * 100 : i * 20)
      .attr('y', d => y(d.value) + this.margin.top)
      .attr('height', d => this.height - this.margin.bottom - y(d.value))

    const maxValue = d3.max(this.sims, d => d.value)
    const maxSeats = this.sims.find(item => item.value === maxValue).seats

    const majorityLine = this.svg.append('line')
      .style('stroke-width', 1.5)
      .style('stroke', '#555')
      .style('fill', 'none')

    majorityLine
      .attr('x1', 0)
      .attr('y1', 0)
      .attr('x2', 0)
      .attr('y2', 0)
      .transition()
      .delay(4)
      .attr('x1', x(this.majority))
      .attr('y1', 0)
      .attr('x2', x(this.majority))
      .attr('y2', this.height - 30)

    const majorityText = this.svg.append('text')
      .attr('fill', '#333')
      .attr('font-size', '0.75em')
      .attr('font-weight', 500)
      .attr('text-anchor', () => {
        if (this.majority < maxSeats) {
          return 'end'
        } else {
          return 'start'
        }
      })
      .style('opacity', 1)
      .style('letter-spacing', '0.07em')
      .text(() => `MAJORITY (${this.majority})`)

    majorityText
      .attr('y', 10)
      .attr('x', 0)
      .transition()
      .delay(1)
      .attr('y', 10)
      .attr('x', () => {
        if (this.majority < maxSeats) {
          return x(this.majority) - 7
        } else {
          return x(this.majority) + 7
        }
      })

    // gop win probability
    this.svg.append('text')
      .attr('class', 'model-info')
      .attr('fill', '#333')
      .attr('font-size', '0.8em')
      .attr('font-weight', 500)
      .attr('text-anchor', 'start')
      .attr('pointer-events', 'none')
      .style('opacity', 1)
      .style('letter-spacing', '0.07em')
      .text(() => 'GOP Majority Probability')
      .attr('x', 10)
      .attr('y', 40)

    this.svg.append('text')
      .attr('class', 'model-info')
      .attr('fill', '#333')
      .attr('font-size', '1em')
      .attr('font-weight', 600)
      .attr('text-anchor', 'start')
      .attr('pointer-events', 'none')
      .style('opacity', 1)
      .style('letter-spacing', '0.05em')
      .text(() => this.gopProb)
      .attr('x', 10)
      .attr('y', 65)

    // movement since last model
    this.svg.append('text')
      .attr('class', 'model-info')
      .attr('fill', '#333')
      .attr('font-size', '0.8em')
      .attr('font-weight', 500)
      .attr('text-anchor', 'start')
      .attr('pointer-events', 'none')
      .style('opacity', 1)
      .style('letter-spacing', '0.07em')
      .text(() => 'Movement')
      .attr('x', 10)
      .attr('y', 105)

    this.svg.append('text')
      .attr('class', 'model-info')
      .attr('fill', '#333')
      .attr('font-size', '0.7em')
      .attr('font-weight', 500)
      .attr('text-anchor', 'start')
      .attr('pointer-events', 'none')
      .style('opacity', 1)
      .style('letter-spacing', '0.07em')
      .text(() => '(Since Last Model)')
      .attr('x', 10)
      .attr('y', 120)

    this.svg.append('text')
      .attr('class', 'model-info')
      .attr('fill', () => {
        if (this.movement.party === 'R') {
          return '#FA3138'
        } else {
          return '#66ABCF'
        }
      })
      .attr('font-size', '1em')
      .attr('font-weight', 600)
      .attr('text-anchor', 'start')
      .style('opacity', 1)
      .style('letter-spacing', '0.05em')
      .text(() => `+${this.movement.diff}% ${this.movement.party}`)
      .attr('x', 10)
      .attr('y', 145)
  },
  visibilityChange (isVisible) {
    if (isVisible && !this.render) {
      this.render = true
    }
  }
}

export default {
  props,
  computed,
  methods,
  data () {
    return {
      width: null,
      height: null,
      margin: { top: 10, right: 30, bottom: 30, left: 40 },
      svg: null,
      render: false,
      info: {
        seats: null,
        simulations: null,
        percentage: null
      }
    }
  },
  mounted () {
    if (document.documentElement.clientWidth < 960) {
      this.margin = { top: 0, right: 10, bottom: 0, left: 10 }
      this.render = true
    }
  },
  watch: {
    screenWidth () {
      if (this.render) {
        this.margin = { top: 10, right: 30, bottom: 30, left: 40 }
        if (this.render && typeof this.simulations[0] === 'object') {
          this.drawChart()
        }
      }
    },
    currentDate () {
      if (this.render && typeof this.simulations[0] === 'object') {
        this.drawChart()
      }
    },
    render () {
      if (this.render && typeof this.simulations[0] === 'object') {
        this.drawChart()
      }
    }
  }
}
</script>

<style>
.histogram-chart-wrapper {
  position: relative;
}

.hover-info {
  position: absolute;
  top: 25px;
  right: 10px;
  pointer-events: none;
}

.info-wrapper {
  text-align: end;
  margin-bottom: 10px;
}

.info-label {
  color: #333;
  font-size: 0.8em;
  font-weight: 500;
  letter-spacing: 0.07em;
}

.info-value {
  color: #333;
  font-size: 1em;
  font-weight: 600;
  letter-spacing: 0.05em;
}

.histogram-chart .x-axis path {
  display: none;
}

.histogram-chart .tick line {
  display: none;
}

.histogram-chart .x-axis .tick text {
  font-size: 1.2em;
  font-weight: 300;
  letter-spacing: 0.08em;
}
</style>



// WEBPACK FOOTER //
// src/components/charts/Histogram.vue